import { useMemo } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import {
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_AXNUI_LAYOUT,
    DEFAULT_AXNUI_THEME
} from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { ThemingValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import {
    AXNUIContent,
    AXNUILayout,
    AXNUITheme,
    MultiLanguageWrapper
} from "@iddataweb/idw-common-ui/types";
import { PageLevelTypes } from "@enums/page-level-types";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { setValueAndAddedToProfileForProfileProperty } from "@utils/login-hint/login-hint-utils";
import { AXNUIThemingPreviewContainer } from "@containers/common/axnui-theming-preview-container/axnui-theming-preview-container";
import { ThemingSubTabContainer } from "@containers/common/theming-sub-tab-container/theming-sub-tab-container";
import { ThemingLayoutTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-layout-tab-container/theming-layout-tab-container";
import { ThemingHeaderTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-header-tab-container/theming-header-tab-container";
import { ThemingFooterTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-footer-tab-container/theming-footer-tab-container";
import { ThemingActionButtonTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-action-button-tab-container/theming-action-button-tab-container";
import { ThemingFormPanelTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-form-panel-tab-container/theming-form-panel-tab-container";
import { ThemingSuccessPageTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-success-page-tab-container/theming-success-page-tab-container";
import { ThemingErrorPageTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-error-page-tab-container/theming-error-page-tab-container";
import { ThemingLandingPageTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/theming-landing-page-tab-container";
import { generateContentProfileProperty } from "@utils/content-util";
import {
    handleAddWorkflowRedirect,
    handleRemoveWorkflowRedirect,
    handleWorkflowRedirectChangeAtIndex
} from "@utils/redirect-urls/redirect-urls-util";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";

type WorkflowDetailsThemingTabProps = {
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    availableLanguages: LocaleTypes[];
    isMultiLanguageEnabled: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    validationErrors: ThemingValidationErrors;
    inheritedAXNUIContent: AXNUIContent;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    workflowProfile: FormattedIdwProfile;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    isDisplayResultsEnabled: boolean;
    workflowProfileKey: string;
    redirectUrlsInformation: RedirectUrlInformation;
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    primaryRpService: FormattedRpServiceConfiguration;
    customLanguages: Array<LocaleTypes>;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
    setWorkflowProfile: (workflowProfile: FormattedIdwProfile) => void;
    setRpServiceConfigurationsState: (rpServiceConfigs: Array<FormattedRpServiceConfiguration>) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
};

export const WorkflowDetailsThemingTab = (props: WorkflowDetailsThemingTabProps) => {
    const {
        isDisabled,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        multiLanguageAXNUIContent,
        validationErrors,
        inheritedAXNUIContent,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        workflowProfile,
        workflowDetailsConfigurationState,
        workflowProfileKey,
        isDisplayResultsEnabled,
        redirectUrlsInformation,
        primaryRpService,
        rpServiceConfigurations,
        customLanguages,
        setRpServiceConfigurationsState,
        updateProperties,
        setWorkflowProfile,
        uploadFileToS3
    } = props;

    const permissionBasedIsDisabled = useMemo(() => {
        return (
            isDisabled ||
            !workflowDetailsConfigurationState.permissions.canModifyAXNUICustomization ||
            !workflowDetailsConfigurationState.permissions.canModifyBranding
        );
    }, [
        isDisabled,
        workflowDetailsConfigurationState.permissions.canModifyAXNUICustomization,
        workflowDetailsConfigurationState.permissions.canModifyBranding
    ]);

    const updateAXNUIContentPropertyValue = (newAXNUIContent: AXNUIContent) => {
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                return generateContentProfileProperty(
                    property,
                    isMultiLanguageEnabled,
                    multiLanguageAXNUIContent,
                    availableLanguages,
                    selectedLanguage,
                    newAXNUIContent,
                    DEFAULT_AXNUI_CONTENT
                );
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    const updateAXNUILayoutPropertyValue = (newAxnuiLayout: AXNUILayout) => {
        const newValue = JSON.stringify(newAxnuiLayout);
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                return setValueAndAddedToProfileForProfileProperty(property, newValue, true);
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    const updateAXNUIThemePropertyValue = (axnuiTheme: AXNUITheme) => {
        const newValue = JSON.stringify(axnuiTheme);
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === AXNUI_THEME_PROPERTY_TYPE_ID) {
                return setValueAndAddedToProfileForProfileProperty(property, newValue, true);
            }
            return property;
        });
        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    const handleRemoveRedirect = (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        handleRemoveWorkflowRedirect(
            workflowProfile,
            workflowProfileKey,
            index,
            redirectUrlOrigin,
            redirectUrlsInformation,
            primaryRpService,
            rpServiceConfigurations,
            setWorkflowProfile,
            setRpServiceConfigurationsState
        );
    };

    const handleAddRedirect = (newRedirectUrl: string) => {
        handleAddWorkflowRedirect(
            workflowProfile,
            workflowProfileKey,
            newRedirectUrl,
            setWorkflowProfile
        );
    };

    const handleRedirectChangeAtIndex = (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        handleWorkflowRedirectChangeAtIndex(
            workflowProfile,
            workflowProfileKey,
            index,
            redirectUrlOrigin,
            newRedirectUrl,
            redirectUrl,
            primaryRpService,
            rpServiceConfigurations,
            isMultiLanguageEnabled,
            availableLanguages,
            setWorkflowProfile,
            setRpServiceConfigurationsState
        );
    };

    const generatePreviewComponent = (currentTab: AXNUIThemingTabTypes, previewableRedirectUrls?: Array<string>): React.ReactNode => {
        //it's not necessary to pass in real values for localizeStrings, attributes, mergeTheming, or useTheming..
        //because we don't display attributes in the workflow view
        //^^ need to revisit this...it might make sense to use real values
        return (
            <AXNUIThemingPreviewContainer
                currentTab={currentTab}
                axnuiContent={axnuiContent}
                axnuiLayout={axnuiLayout}
                axnuiTheme={axnuiTheme}
                axnAttributeTypeFieldCustomizations={[]}
                axnAttributeTypeFields={[]}
                attributes={[]}
                localizeStrings={false}
                mergeThemingAndLocalizedFile={false}
                activeLanguage={selectedLanguage}
                redirectUrls={previewableRedirectUrls}
                hasDisplayableDocumentCaptureAttributes={false}
                previewEntityType={PreviewEntityTypes.WORKFLOW}
                hasCancelButtonURLEnabled={false}
            />
        );
    };


    return (
        <ThemingSubTabContainer>
            <Tabs id="theming" unmountOnExit={true}>
                <Tab eventKey={AXNUIThemingTabTypes.LAYOUT} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.tabName}>
                    <ThemingLayoutTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUITheme={DEFAULT_AXNUI_THEME}
                        inheritedAXNUILayout={DEFAULT_AXNUI_LAYOUT}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        customLanguages={customLanguages}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                {axnuiLayout.isHeaderEnabled && (
                    <Tab eventKey={AXNUIThemingTabTypes.HEADER} title={validationErrors.header.tabTitle}>
                        <ThemingHeaderTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.header}
                            axnuiContent={axnuiContent}
                            axnuiTheme={axnuiTheme}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            inheritedAXNUITheme={DEFAULT_AXNUI_THEME}
                            pageLevel={PageLevelTypes.WORKFLOW}
                            generatePreviewComponent={generatePreviewComponent}
                            uploadFileToS3={uploadFileToS3}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                        />
                    </Tab>
                )}
                {axnuiLayout.isFooterEnabled && (
                    <Tab eventKey={AXNUIThemingTabTypes.FOOTER} title={validationErrors.footer.tabTitle}>
                        <ThemingFooterTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.footer}
                            axnuiContent={axnuiContent}
                            axnuiTheme={axnuiTheme}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            inheritedAXNUITheme={DEFAULT_AXNUI_THEME}
                            pageLevel={PageLevelTypes.WORKFLOW}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                        />
                    </Tab>
                )}
                <Tab eventKey={AXNUIThemingTabTypes.ACTION_BUTTON} title={validationErrors.actionButton.tabTitle}>
                    <ThemingActionButtonTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.actionButton}
                        axnuiContent={axnuiContent}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUITheme={DEFAULT_AXNUI_THEME}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        hasDisplayableDocumentCaptureAttributes={workflowDetailsConfigurationState.hasDCUIService}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.FORM_PANEL} title={validationErrors.formPanel.tabTitle}>
                    <ThemingFormPanelTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.formPanel}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={DEFAULT_AXNUI_LAYOUT}
                        inheritedAXNUITheme={DEFAULT_AXNUI_THEME}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.SUCCESS_PAGE} title={validationErrors.successPage.tabTitle}>
                    <ThemingSuccessPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.successPage}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={DEFAULT_AXNUI_LAYOUT}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.ERROR_PAGE} title={validationErrors.errorPage.tabTitle}>
                    <ThemingErrorPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.errorPage}
                        axnuiContent={axnuiContent}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.LANDING_PAGE} title={validationErrors.landingPage.tabTitle}>
                    <ThemingLandingPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        isServiceBelongToWorkflow={false}
                        validationErrors={validationErrors.landingPage}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        isDisplayResultsEnabled={isDisplayResultsEnabled}
                        inheritedIsDisplayResultsEnabled={isDisplayResultsEnabled}
                        redirectUrlsInformation={redirectUrlsInformation}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={DEFAULT_AXNUI_LAYOUT}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        generatePreviewComponent={generatePreviewComponent}
                        handleAddRedirect={handleAddRedirect}
                        handleRemoveRedirect={handleRemoveRedirect}
                        handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
                        updateProperties={updateProperties}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                    />
                </Tab>
            </Tabs>
        </ThemingSubTabContainer>
    );
};